import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../supabase/client'
import './DashboardPage.css'

const DashboardPage = ({ userId }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const hasCheckedSession = useRef(false)
  const [currentUserUuid, setCurrentUserUuid] = useState<string | null>(null)

  // Функция для загрузки списка профилей
  const fetchProfiles = async () => {
    try {
      console.log('Загрузка списка профилей...')
      setLoading(true)
      const { data: profilesData, error: fetchError } = await supabase
        .from('ipr_profiles')
        .select('*')
      if (fetchError) {
        console.error('Error fetching profiles:', fetchError)
        throw fetchError
      }
      console.log('Получено профилей:', profilesData?.length)
      setProfiles(profilesData || [])
    } catch (err) {
      console.error('Ошибка при загрузке профилей:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Загрузка списка профилей при монтировании компонента и при каждом изменении location.key
  // location.key меняется автоматически при каждой навигации
  useEffect(() => {
    console.log('DashboardPage: обновление данных, location.key=', location.key)

    // Очищаем временные данные создания профиля
    localStorage.removeItem('userDataResults')

    fetchProfiles()
  }, [location.key]) // Зависимость от location.key заставит эффект запускаться при каждой навигации

  useEffect(() => {
    if (!hasCheckedSession.current) {
      hasCheckedSession.current = true
      supabase.auth
        .getSession()
        .then(({ data: { session }, error }) => {
          if (error) {
            // Log error if needed, but removing all debug logs as per request
          } else if (!session) {
            navigate('/login')
          } else {
            // Session found, no action needed
          }
        })
        .catch((err) => {
          // Handle error silently or log if critical
        })
    }
  }, [navigate]) // Dependency on navigate, but ref ensures single execution

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserUuid(user?.id || null);
    };
    fetchUser();
  }, []);

  // Обработчик для создания нового профиля
  const handleCreateNewProfile = () => {
    // Сохраняем ID пользователя в localStorage для использования в процессе создания
    localStorage.setItem('currentUserId', userId || 'demo')
    // Очищаем временные данные предыдущих сессий
    localStorage.removeItem('userDataResults')

    // Перенаправляем на первый шаг создания профиля
    navigate('/personal-info')
  }

  // Обработчик для просмотра профиля
  const handleViewProfile = (profileId) => {
    // Очищаем временные данные перед переходом на страницу профиля
    localStorage.removeItem('userDataResults')
    console.log(`Переход к профилю: ${profileId}`)
    navigate(`/profile/${profileId}`)
  }

  // Обработчик для редактирования профиля
  const handleEditProfile = (profileId) => {
    // Загружаем данные профиля и затем направляем пользователя через процесс редактирования
    navigate(`/edit-profile/${profileId}`)
  }

  // Обработчик для удаления профиля
  const handleDeleteProfile = async (profileId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот профиль?')) {
      try {
        const { error: deleteError } = await supabase
          .from('ipr_profiles')
          .delete()
          .eq('userId', profileId)
        if (deleteError) {
          console.error('Error deleting profile:', deleteError)
          throw deleteError
        }

        // Обновляем список профилей после удаления
        setProfiles(profiles.filter((profile) => profile.userId !== profileId))
      } catch (err) {
        console.error('Ошибка при удалении профиля:', err)
        setError(err.message)
      }
    }
  }

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">
          <div className="loader"></div>
          <p>Загрузка профилей...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Личный кабинет</h1>
        <button className="btn-primary" onClick={handleCreateNewProfile}>
          Создать новый профиль
        </button>
      </div>

      {error && (
        <div className="dashboard-error">
          <p>{error}</p>
        </div>
      )}

      <div className="dashboard-content">
        <h2>Ваши профили</h2>

        {profiles.length === 0 ? (
          <div className="no-profiles">
            <p>У вас еще нет сохраненных профилей.</p>
            <button className="btn" onClick={handleCreateNewProfile}>
              Создать первый профиль
            </button>
          </div>
        ) : (
          <div className="profiles-grid">
            {profiles.map((profile) => (
              <div key={profile.userId} className="profile-card">
                <div className="profile-header">
                  <div className="profile-avatar">
                    {profile.firstName && profile.lastName
                      ? profile.firstName[0] + profile.lastName[0]
                      : '👤'}
                  </div>
                  <div className="profile-info">
                    <h3>
                      {profile.firstName} {profile.lastName}
                    </h3>
                    <p>
                      {profile.position} {profile.company ? `• ${profile.company}` : ''}
                    </p>
                  </div>
                </div>

                <div className="profile-footer">
                  <button className="btn-view" onClick={() => handleViewProfile(profile.userId)}>
                    Просмотр
                  </button>
                  {currentUserUuid === profile?.uuid && (
                    <>
                      <button className="btn-edit" onClick={() => handleEditProfile(profile.userId)}>
                        Редактировать
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDeleteProfile(profile.userId)}
                      >
                        Удалить
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default DashboardPage
